/**
 * Helpers de formatação pt-BR.
 *
 * BRL chega como string do DRF (Decimal). Quebrar em parte inteira/centavos
 * permite renderizar centavos com peso visual menor (estilo editorial).
 */

const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'short',
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export function parseDecimal(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

export interface BrlParts {
  integer: string;
  cents: string;
}

export function splitBrl(value: string | number | null | undefined): BrlParts {
  const numeric = parseDecimal(value);
  const [integer, cents = '00'] = BRL_FORMATTER.format(numeric).split(',');
  return { integer: integer ?? '0', cents: (cents || '00').padEnd(2, '0').slice(0, 2) };
}

export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return DATE_FORMATTER.format(new Date(iso));
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return DATE_TIME_FORMATTER.format(new Date(iso));
}

const DAY_MONTH_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
});

const CALENDAR_DATE = /^\d{4}-(\d{2})-(\d{2})$/;

/**
 * "20/10": o dia e o mês de uma data (`AAAA-MM-DD`, como o vencimento da guia) ou de um instante,
 * no fuso do aparelho. A data é lida como o dia do calendário: `new Date('2026-10-20')` é a
 * meia-noite UTC, que no Brasil ainda é o dia 19.
 */
export function formatDayMonth(value: string): string {
  const calendar = CALENDAR_DATE.exec(value);
  if (calendar) return `${calendar[2]}/${calendar[1]}`;
  return DAY_MONTH_FORMATTER.format(new Date(value));
}

const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

const DATE_WITH_YEAR_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
/** Até uma semana a data é relativa ("há 3 dias"); depois disso, o dia do calendário. */
const RELATIVE_DAYS_LIMIT = 7;

/**
 * "agora", "há 5 minutos", "há 3 horas", "ontem", "há 3 dias" e, passada uma semana, a data
 * ("12 de set. de 2026"). Um instante no futuro (relógio do aparelho atrasado) é "agora".
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const elapsed = now.getTime() - Date.parse(iso);
  if (elapsed < MINUTE_MS) return 'agora';
  if (elapsed < HOUR_MS) {
    return RELATIVE_TIME_FORMATTER.format(-Math.floor(elapsed / MINUTE_MS), 'minute');
  }
  if (elapsed < DAY_MS) {
    return RELATIVE_TIME_FORMATTER.format(-Math.floor(elapsed / HOUR_MS), 'hour');
  }
  const days = Math.floor(elapsed / DAY_MS);
  if (days < RELATIVE_DAYS_LIMIT) return RELATIVE_TIME_FORMATTER.format(-days, 'day');
  return DATE_WITH_YEAR_FORMATTER.format(new Date(iso));
}

export function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}
